import { useEffect } from 'react';

import { Button, formatNumber, PageTitle } from '@dec/shared';
import { Combobox, Datepicker, HookFormResolver, Number, Text, TextArea } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { File } from 'app/components/form';
import { factoryYup, limit } from 'app/core/factory-yup';
import { DateRequired, NumberRequired } from 'app/core/validations/common';
import { useGetApplication } from 'app/hooks';
import i18next from 'i18next';
import { Form, FormControl, InputGroup } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCatalogValueList } from '../../configuration/catalogs/hooks/useCatalogValueList';
import { type CatalogModel, catalogName } from '../../configuration/catalogs/models/Catalog';
import { type ReconciliationOfPayment } from '../models/Payment';

const yup = factoryYup();

const catalogIdInternational = '6b36a4ef-8bb4-42d3-98e3-03cec11f6927';
const validations = yup.object().shape({
  currency: yup.object().nullable().required(i18next.t('common.validations.fieldRequired')),
  paymentMethod: yup.object().nullable().required(i18next.t('common.validations.fieldRequired')),
  // attachment: yup.object().required(i18next.t('common.validations.fieldRequired')),
  paymentDate: DateRequired,
  reference: yup.string().required(() => i18next.t('common.validations.fieldRequired')),
  exchangeRate: yup
    .number()
    .typeError(() => i18next.t('common.validations.fieldRequired'))
    .required(() => i18next.t('common.validations.fieldRequired')),
  amount: NumberRequired,
  swift: yup.string().nullable().max(limit.maxDescription),
  reason: yup.string().when('reject', {
    is: (reject: any) => reject === 'N',
    then: (schema) => schema.optional(),
    otherwise: (schema) =>
      schema.required(i18next.t('common.validations.fieldRequired')).max(limit.maxDescription)
  })
});

interface Props {
  nameForm: string;
  readOnly?: boolean;
  reject?: boolean;
  initialValues?: {
    paymentMethod: CatalogModel;
    currency: CatalogModel;
    amount: number;
    attachment: any;
    paymentDate?: Date;
    reference: string;
    swift?: string;
    state?: string;
    reason?: string;
    reject?: boolean;
    reconciliationOfPayments?: ReconciliationOfPayment;
  };
  onSubmit: (value: any) => void;
}

const Exchange = ({ currency, date }: { currency?: any; date?: Date }) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const [{ loading, error, data }, execute] = useGetApplication<{ valueCurrency: number }>(
    {
      url: '/exchangerate',
      params: { currency: currency?.code, date }
    },
    false
  );

  const amount = watch('amount');
  const exchangeRate = watch('exchangeRate');

  useEffect(() => {
    if (currency && date) {
      execute();
    }
  }, [currency, date, execute]);

  useEffect(() => {
    if (data) {
      const nacionalAmount =
        (data?.valueCurrency ? data?.valueCurrency : 0) * (amount ? amount : 0);
      setValue('exchangeRate', data.valueCurrency);
      setValue('nacionalAmount', nacionalAmount);
    }
  }, [data, amount]);

  const labelExchageRate = t('taxpayer-payments.label.exchange-rate');
  const labelLocalAmount = t('taxpayer-payments.label.local-amount');

  return (
    <Row className={exchangeRate === undefined || exchangeRate === 1 ? 'd-none' : ''}>
      <Col>
        <Number
          readonly={true}
          allowDecimal={true}
          includeThousandsSeparator={true}
          name="exchangeRate"
          label={labelExchageRate}
        />
      </Col>
      <Col>
        <Number
          readonly={true}
          allowDecimal={true}
          includeThousandsSeparator={true}
          name="nacionalAmount"
          label={labelLocalAmount}
        />
      </Col>
    </Row>
  );
};

const ReconciliationOfPaymentForm = ({
  register,
  reconciliationOfPayment
}: {
  register: any;
  reconciliationOfPayment: ReconciliationOfPayment;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labelDate = t('taxpayer-payments.label.date-payment');
  const labelAmount = t('taxpayer-payments.label.amount');
  const labelPaymentReference = t('common.label.paymentReference');
  const labelSwiftNumber = t('taxpayer-payments.label.swiftNumber');
  const labelUploadNumber = t('taxpayer-payments.label.uploadNumber');
  const goTo = () => {
    navigate(
      `/taxpayer/check-load-records/${reconciliationOfPayment?.paymentsReceived?.paymentReceived?.id}`
    );
  };

  return (
    <>
      <PageTitle title={t('common.taxreturn-status.reconciled')} />

      <Row>
        <Col xl={6} lg={6} md={6}>
          <Form.Group>
            <Form.Label>{labelUploadNumber}</Form.Label>
            <InputGroup className="mb-3">
              <FormControl
                value={reconciliationOfPayment?.paymentsReceived?.paymentReceived?.uploadNumber}
                name="taxReturnNumber"
                readOnly={true}
                {...register('taxReturnNumber')}
              />
              <Button onClick={goTo} icon={Icon.Search} text="" variant="primary" />
            </InputGroup>
          </Form.Group>
        </Col>
        <Col>
          <Datepicker
            defaultValue={reconciliationOfPayment?.paymentsReceived?.date}
            readOnly={true}
            label={labelDate}
            name="paymentDateReconciled"
          />
        </Col>
        <Col>
          <Number
            readonly={true}
            allowDecimal={true}
            defaultValue={formatNumber(reconciliationOfPayment?.paymentsReceived?.amount) as any}
            includeThousandsSeparator={true}
            name="amountReconciled"
            label={labelAmount}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Text
            defaultValue={reconciliationOfPayment?.paymentsReceived?.swift}
            readonly={true}
            name="swiftReconciled"
            label={labelSwiftNumber}
          />
        </Col>
        <Col>
          <Text
            defaultValue={reconciliationOfPayment?.paymentsReceived?.reference}
            readonly={true}
            name="referenceReconciled"
            label={labelPaymentReference}
          />
        </Col>
      </Row>
    </>
  );
};

export const FormPaymentDeclaration = ({
  nameForm,
  onSubmit,
  readOnly,
  initialValues,
  reject
}: Props) => {
  const {
    isLoading: loading,
    error,
    data: currencies
  } = useCatalogValueList(catalogName.taxReturnCurrencies);
  const {
    isLoading: loadingPaymentMethod,
    error: errorPaymentMethod,
    data: paymentMethods
  } = useCatalogValueList(catalogName.paymentMethod);

  const isLoading = loading || loadingPaymentMethod || error || errorPaymentMethod;
  const { t } = useTranslation();

  if (isLoading) {
    return <></>;
  }

  const labelCurrency = t('common.label.currency');
  const labelPaymentMethod = t('common.label.paymentMethod');
  const labelAttachment = t('common.label.supportDocument');
  const labelReference = t('common.label.paymentReference');
  const labelAmount = t('taxpayer-payments.label.amount');
  const labelSwift = t('taxpayer-payments.label.swiftNumber');
  const labelReason = t('taxpayer-request-val.label.reason');

  return (
    <HookFormResolver
      initialValues={initialValues}
      validations={validations}
      nameForm={nameForm}
      onSubmit={onSubmit}
    >
      {({ watch, register }: HookFormMethods) => {
        const currency = watch('currency');
        const paymentDate = watch('paymentDate');
        const paymentMethod = watch('paymentMethod');
        return (
          <>
            <input
              defaultValue={initialValues?.reject ? 'S' : 'N'}
              {...register('reject')}
              hidden={true}
            />
            <Row>
              <Col>
                <Combobox
                  defaultValue={initialValues?.paymentMethod}
                  readOnly={readOnly}
                  code="id"
                  display="name"
                  data={paymentMethods}
                  label={labelPaymentMethod}
                  name="paymentMethod"
                />
              </Col>
              <Col>
                <Datepicker label={t('taxpayer-payments.label.date-payment')} name="paymentDate" />
              </Col>
            </Row>
            <Row>
              <Col>
                <Combobox
                  defaultValue={initialValues?.currency}
                  readOnly={readOnly}
                  code="id"
                  display="name"
                  data={currencies}
                  label={labelCurrency}
                  name="currency"
                />
              </Col>
              <Col>
                <Number
                  allowDecimal={true}
                  includeThousandsSeparator={true}
                  name="amount"
                  label={labelAmount}
                />{' '}
              </Col>
            </Row>
            <Exchange
              date={initialValues?.paymentDate ? initialValues.paymentDate : paymentDate}
              currency={initialValues?.currency ? initialValues.currency : currency}
            />
            <Row>
              <Col>
                <File
                  defaultValue={initialValues?.attachment}
                  label={labelAttachment}
                  accept="application/pdf"
                  name="attachment"
                  readonly={readOnly}
                />
              </Col>
            </Row>
            {paymentMethod?.id === catalogIdInternational && (
              <Row>
                <Col>
                  <Text readonly={readOnly} name="swift" label={labelSwift} />
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                <TextArea readOnly={readOnly} name="reference" label={labelReference} />
              </Col>
            </Row>

            {(initialValues?.reason != undefined || reject) && (
              <Row>
                <Col>
                  <TextArea
                    readOnly={initialValues?.state === 'rejected'}
                    name="reason"
                    label={labelReason}
                  />
                </Col>
              </Row>
            )}
            {initialValues?.reconciliationOfPayments && (
              <ReconciliationOfPaymentForm
                register={register}
                reconciliationOfPayment={initialValues?.reconciliationOfPayments as any}
              />
            )}
          </>
        );
      }}
    </HookFormResolver>
  );
};
